import React from "react"
import PortableText from "@sanity/block-content-to-react"
import { Link } from "gatsby"
import urlBuilder from "@sanity/image-url"
import BlockContentFigure from "./block content/BlockContentFigure"
import { FaFileDownload } from "react-icons/fa"
import ReactPlayer from "react-player"
import InlineAlbum from "./albums/InlineAlbum"

const InternalLink = ({ linkProps, preSlug }) => (
  <span>
    <Link
      to={`/${preSlug}/${linkProps.mark.item.slug.current}`}
      className="underline hover:text-primary-dark visited:text-purple-600"
    >
      {linkProps.children}
    </Link>
  </span>
)

const InlineDownload = ({ downloadProps }) => {
  return (
    <span className="inline-flex gap-x-2 font-bold underline hover:font-normal">
      <a href={downloadProps.mark.file.asset.url}>{downloadProps.children}</a>
      <FaFileDownload />
    </span>
  )
}

const BlockContentPrimary = ({ blockData }) => {
  // const urlFor = source =>
  //   urlBuilder({
  //     projectId: process.env.GATSBY_SANITY_ID,
  //     dataset: process.env.GATSBY_SANITY_DATASET,
  //   }).image(source)
  //   Serializer is used to tell component how to render Portable Text
  //   if no serializer is used, a default serializer is used under-the hood
  //   This allows us to customize how modify defaults and render custom formats
  const serializer = {
    marks: {
      syllabics: ({ children }) => (
        <span className=" text-secondary-light font-syllabics">{children}</span>
      ),
      link: props => {
        if (props.mark.blank) {
          return (
            <a
              href={props.mark.href}
              target="_blank"
              rel="noopener noreferrer"
              className="blockContent__link"
            >
              {props.children}
            </a>
          )
        }
        return (
          <a href={props.mark.href} className="blockContent__link">
            {props.children}
          </a>
        )
      },
      internalPostLink: props => {
        return <InternalLink linkProps={props} preSlug="pages" />
      },
      linkToBlogPost: props => {
        return <InternalLink linkProps={props} preSlug="news" />
      },
      fileDownload: props => {
        return <InlineDownload downloadProps={props} />
      },
    },
    types: {
      inlineImage: props => {
        const sanityImageId = props.node.asset.id
        const alt = props?.node?.alt || props?.node?.asset?.originalFilename
        const isFullWidth = !!props.node.isFullWidth
        const isGraphicImage = !!props.node.isGraphicImage

        return (
          <BlockContentFigure
            caption={props?.node?.caption}
            imageAssetId={sanityImageId}
            alt={alt}
            maxWidth={props.node.isFullWidth ? 1500 : 500}
            isFullWidth={isFullWidth}
            isGraphicImage={isGraphicImage}
          />
        )
      },
      block: props => {
        const { style = "normal" } = props.node

        // Adds classes to headers so I can target them in the css file (layout.scss)
        //  see https://github.com/sanity-io/block-content-to-react#customizing-the-default-serializer-for-block-type
        if (/^h\d/.test(style)) {
          const level = style.replace(/[^\d]/g, "")
          return React.createElement(
            style,
            { className: `blockContent-heading-${level}` },
            props.children
          )
        }

        // Fall back to default handling
        return PortableText.defaultSerializers.types.block(props)
      },
      youtube: ({ node }) => {
        const { url, title } = node
        return (
          <div className="w-full flex justify-center mb-5">
            <div className={`w-full max-w-xl `}>
              <h2>{title}</h2>
              <div className={`relative `} style={{ paddingTop: "56.25%" }}>
                <ReactPlayer
                  url={url}
                  width="100%"
                  height="100%"
                  className={`absolute top-0 left-0 `}
                  controls={true}
                />
              </div>
            </div>
          </div>
        )
      },
      // inline photo album
      photoAlbum: ({ node }) => {
        const { album } = node

        return <InlineAlbum albumData={album} />
      },
    },
    list: props => {
      return props.type === "bullet" ? (
        <ul>{props.children}</ul>
      ) : (
        <ol>{props.children}</ol>
      )
    },
  }

  return <PortableText blocks={blockData} serializers={serializer} />
}

export default BlockContentPrimary
